/**
 * The "dashboard view" export: for every stack and every fleet-wide view, the
 * figures the dashboard shows, already worked out for each time window.
 *
 * Every figure comes from the same selectors the screen uses, and every view
 * is built by the same function as the page (`viewForSelection`), so an
 * export cannot sum a different set of stacks from the screen or show a
 * standalone customer's whole AWS bill as Numa cost.
 *
 * Inactive stacks follow the screen: a view hides them unless "Show inactive"
 * is on, and their spend stays in its spend figures.
 *
 * `scopes` in each window view says which figures follow the window on that
 * snapshot and which are lifetime totals.
 */
import type {
  ByClientRow,
  ClientSnapshot,
  DashboardView,
  StaleStack,
  TopConversation,
  TopScheduledRun,
  UserAppsInventory,
} from '@/types/fleetAnalytics';
import { snapshotContract } from './contracts';
import type { ContractOf } from './contracts';
import {
  agentRowKey,
  buildByClientRowsWindowed,
  fleetViews,
  hiddenBy,
  isAggregate,
  isInfraAccount,
  modelTotalsInWindow,
  productOf,
  setAllData,
  setLastNDays,
  snapshotForDisplay,
  sumByKeyInWindow,
  topConvInWindow,
  topRunInWindow,
  viewForSelection,
  windowDays,
} from './shared';
import type { ViewOptions, WindowState } from './shared';
import type { FleetTier } from './fleetData';
import { buildInferredServiceMix, computeInferredCosts } from './inferredCosts';
import {
  isReady,
  selectActiveUsers,
  selectApps,
  selectCategories,
  selectChat,
  selectCredits,
  selectInactiveSpend,
  selectRequests,
  selectScheduled,
  selectTimeSaved,
  selectTokens,
  selectTopTools,
} from './selectors';
import type { Scope } from './ui/kit';

const DEFAULT_WINDOW_DAYS = [7, 14, 30, 60, 90] as const;
const TOP_N_USERS = 20;
const TOP_N_AGENTS = 20;
const TOP_N_CONVERSATIONS = 50;
const TOP_N_RUNS = 50;

export interface DashboardWindowView {
  window: { start: string; end: string; days: number; label: string };
  spend: {
    inferred_total: number;
    raw_ce_total: number;
    chat_llm: number;
    ce_claude: number;
    lent: number;
    borrowed: number;
    role: 'standalone' | 'borrower' | 'lender' | 'mixed';
    /** False when the model lines of an account could not be worked out: `inferred_total` is then too high. */
    model_lines_known: boolean;
    /**
     * Summed views only: the part of the figures above that belongs to the
     * Inactive stacks the view hides. Absent when it hides none or they spent nothing.
     */
    inactive?: { stacks: string[]; inferred_total: number; raw_ce_total: number; chat_llm: number };
    avg_cost_per_conv: number;
    cost_per_user_msg: number;
    scheduled_runs_count: number;
    scheduled_runs_cost: number;
  };
  unit_economics: {
    plain_chat: { cost: number; user_msgs: number; convs: number; cost_per_msg: number; cost_per_conv: number };
    agent_chat: { cost: number; user_msgs: number; convs: number; cost_per_msg: number; cost_per_conv: number };
    agent_run: { cost: number; convs: number; cost_per_run: number };
  };
  volume: {
    conversations: number;
    adhoc_convs: number;
    scheduled_convs: number;
    user_messages: number;
    tool_calls: number;
    agentic_turns: number;
  };
  operations: {
    /** People with a message in the window. Null when the per-person data was not loaded. */
    active_users: number | null;
    provisioned_users?: number;
    cache_hit_ratio_pct: number | null;
    requests: number;
    errors: number;
    failed_runs: number;
    time_saved_minutes: number;
    active_schedules?: number;
    total_schedules?: number;
  };
  /**
   * The period each figure that can fall back covers on this snapshot.
   * `window`: it follows the window. `all-time`: the snapshot has no daily
   * record for it, and the number is the lifetime total.
   */
  scopes: {
    conversations: 'counted once, on the first day' | 'counted once for each active day';
    by_category: Scope;
    active_users: Scope | 'not loaded';
    tokens: Scope;
    requests_and_errors: Scope;
    failed_runs: Scope;
    time_saved: Scope;
    by_tool: Scope;
    by_model: Scope;
    cost_composition: Scope;
  };
  cost_composition: Record<string, number>;
  by_category: {
    agent_run: { convs: number; cost: number; user_messages: number };
    agent_chat: { convs: number; cost: number; user_messages: number };
    plain_chat: { convs: number; cost: number; user_messages: number };
  };
  by_user_top: Array<{ user_id: string; client?: string; cost: number; messages: number }>;
  by_agent_top: Array<{
    agent_id: string;
    client?: string;
    agent_title?: string | null;
    cost: number;
    scheduled_cost: number;
    adhoc_cost: number;
  }>;
  by_tool: Record<string, number>;
  by_model: Record<string, { cost: number; convs: number }>;
  top_conversations: TopConversation[];
  top_scheduled_runs: TopScheduledRun[];
  /** Absent when no stack in the view meters credits. */
  credits?: {
    available: boolean;
    used_in_window: number;
    current_month?: string;
    current_month_used?: number;
    current_month_allocation?: number;
    /** One stack: the top-up balance. A summed view: prepaid minus owed, clients on credits only. */
    available_balance?: number;
    credit_usd?: number;
    /** One stack: Arcanum can invoice it for credits used past its allocation. */
    collectible?: boolean;
    /** Summed views only: credits clients on credits owe. */
    owed?: number;
    /** Summed views only: credit clients on credits paid for and did not use yet. */
    prepaid?: number;
    /** Credits used this month past the allocation by stacks Arcanum does not invoice for credits. Never owed. */
    not_billed_over?: number;
    /** Summed views only: the metered stacks Arcanum can invoice for credits. */
    collectible_stacks?: number;
    /** Summed views only. */
    clients_with_ledger?: number;
    /** Summed views only: stacks left out of `current_month_used` because their update is from an older month. */
    stacks_behind?: string[];
  };
  /**
   * Apps and dashboards. Absent when the snapshot has no apps record. In this
   * block a key that is absent or null is NOT AVAILABLE for the client, never
   * zero. A key with 0 is a real zero.
   */
  apps?: {
    inventory: UserAppsInventory;
    has_history: boolean;
    history_from?: string;
    runs_in_window?: number | null;
    credits_in_window?: number | null;
    publishes_in_window?: number | null;
  };
  /** Summed views only: one row per stack, highest AWS bill first. */
  by_client?: ByClientRow[];
}

export interface DashboardExportClientEntry {
  client: string;
  is_aggregate: boolean;
  client_config?: ClientSnapshot['client_config'];
  /**
   * A stack: its contract as the screen reads it, from the portal's live
   * record. `client_config` holds the copy from the night of the snapshot.
   */
  contract?: {
    contract: string | null;
    partner: string | null;
    billing: string | null;
    collectible: boolean;
    inactive: boolean;
    product: 'nolia' | 'numa';
  };
  /** A stack: when its snapshot was gathered. A summed view: its OLDEST snapshot. */
  generated_at?: string;
  /** Summed views only. */
  newest_generated_at?: string;
  /** Summed views only: stacks whose data is old. Their numbers are still in the sums. */
  stale_stacks?: StaleStack[];
  window_views: Record<string, DashboardWindowView>;
}

export interface DashboardExportBundle {
  exported_at: string;
  schema_version: '1.2';
  window_presets: number[];
  /** Which tier files were loaded when the export was made. A figure that needs another reads "not loaded". */
  tiers_loaded: FleetTier[];
  views: Record<string, DashboardExportClientEntry>;
}

/**
 * One view inside one window. `data` is a view as the page draws it: a summed
 * view from `viewForSelection`, or one stack through `snapshotForDisplay`.
 * `contractOf` gives each stack's contract, for the credits and the per-stack rows.
 */
export function buildDashboardWindowView(
  data: DashboardView,
  win: WindowState,
  snapshots: ClientSnapshot[],
  contractOf: ContractOf = snapshotContract
): DashboardWindowView {
  const chat = data.chat;
  const ce = data.cost_explorer;
  const inferred = computeInferredCosts(data, win);
  const chatFigures = selectChat(data, win);
  const categories = selectCategories(data, win);
  const scheduled = selectScheduled(data, win);
  const tokens = selectTokens(data, win);
  const requests = selectRequests(data, win);
  const timeSaved = selectTimeSaved(data, win);
  const activeUsers = selectActiveUsers(data, win);
  const tools = selectTopTools(data, win, Number.MAX_SAFE_INTEGER);

  const category = (key: 'plain_chat' | 'agent_chat' | 'agent_run') => categories.rows.find((r) => r.key === key)!;
  const plain = category('plain_chat');
  const agentChat = category('agent_chat');
  const agentRun = category('agent_run');

  const cost_composition = ce?.by_service_daily
    ? buildInferredServiceMix(data, win)
    : { ...(ce?.totals_by_service || {}) };

  // Zero-cost keys are in the sums now, so "top" must drop them itself.
  const userCostInWin = sumByKeyInWindow(chat?.daily_cost_by_user, win);
  const userMsgsInWin = sumByKeyInWindow(chat?.daily_messages_by_user, win);
  const by_user_top = Object.entries(userCostInWin)
    .filter(([, cost]) => cost > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, TOP_N_USERS)
    .map(([user_id, cost]) => ({
      user_id,
      client: chat?.by_user?.[user_id]?.client,
      cost,
      messages: userMsgsInWin[user_id] || 0,
    }));

  // In a summed view an agent is keyed by its stack and its id: one id can be in many stacks.
  const agentCostInWin = sumByKeyInWindow(chat?.daily_cost_by_agent, win);
  const schedCostByAgentInWin = sumByKeyInWindow(chat?.daily_scheduled_cost_by_agent, win);
  const adhocCostByAgentInWin = sumByKeyInWindow(chat?.daily_adhoc_cost_by_agent, win);
  const by_agent_top: DashboardWindowView['by_agent_top'] = (chat?.by_agent || [])
    .map((row) => {
      const key = agentRowKey(row);
      return {
        agent_id: row.agent_id,
        client: row.client,
        agent_title: row.agent_title,
        cost: agentCostInWin[key] || 0,
        scheduled_cost: schedCostByAgentInWin[key] || 0,
        adhoc_cost: adhocCostByAgentInWin[key] || 0,
      };
    })
    .filter((r) => r.cost > 0)
    .sort((a, b) => b.cost - a.cost)
    .slice(0, TOP_N_AGENTS);

  const top_conversations = (chat?.top_conversations || [])
    .filter((c) => topConvInWindow(c, win))
    .slice(0, TOP_N_CONVERSATIONS);
  const top_scheduled_runs = (data.scheduled_runs?.top_runs || [])
    .filter((r) => topRunInWindow(r, win))
    .slice(0, TOP_N_RUNS);

  const view: DashboardWindowView = {
    window: { start: win.startDate, end: win.endDate, days: windowDays(win), label: win.label },
    spend: {
      inferred_total: inferred.totals.inferred,
      raw_ce_total: inferred.totals.ceTotal,
      chat_llm: inferred.totals.chat,
      ce_claude: inferred.totals.ceClaude,
      lent: inferred.totals.lent,
      borrowed: inferred.totals.borrowed,
      role: inferred.role,
      model_lines_known: inferred.modelLinesKnown,
      inactive: inactiveSpendExport(data, win),
      avg_cost_per_conv: chatFigures.costPerConversation.value ?? 0,
      cost_per_user_msg: chatFigures.costPerMessage.value ?? 0,
      scheduled_runs_count: scheduled.runs.value,
      scheduled_runs_cost: scheduled.cost.value,
    },
    unit_economics: {
      plain_chat: {
        cost: plain.cost,
        user_msgs: plain.messages,
        convs: plain.conversations,
        cost_per_msg: plain.costPerMessage ?? 0,
        cost_per_conv: plain.costPerConversation ?? 0,
      },
      agent_chat: {
        cost: agentChat.cost,
        user_msgs: agentChat.messages,
        convs: agentChat.conversations,
        cost_per_msg: agentChat.costPerMessage ?? 0,
        cost_per_conv: agentChat.costPerConversation ?? 0,
      },
      agent_run: {
        cost: agentRun.cost,
        convs: agentRun.conversations,
        cost_per_run: agentRun.costPerConversation ?? 0,
      },
    },
    volume: {
      conversations: chatFigures.conversations.value,
      adhoc_convs: chatFigures.adhocConversations.value,
      scheduled_convs: chatFigures.scheduledConversations.value,
      user_messages: chatFigures.messages.value,
      tool_calls: chatFigures.toolCalls.value,
      agentic_turns: chatFigures.turns.value,
    },
    operations: {
      active_users: isReady(activeUsers) ? activeUsers.value : null,
      provisioned_users: data.users?.provisioned_users,
      cache_hit_ratio_pct: tokens.cacheHitRatio === null ? null : 100 * tokens.cacheHitRatio,
      requests: requests.requests.value,
      errors: requests.errors.value,
      failed_runs: scheduled.failed.value,
      time_saved_minutes: timeSaved.value,
      active_schedules: data.schedules?.by_derived_status?.active,
      total_schedules: data.schedules?.count,
    },
    scopes: {
      conversations: chatFigures.conversations.approx
        ? 'counted once for each active day'
        : 'counted once, on the first day',
      by_category: categories.scope,
      active_users: isReady(activeUsers) ? activeUsers.scope : 'not loaded',
      tokens: tokens.scope,
      requests_and_errors: requests.requests.scope,
      failed_runs: scheduled.failed.scope,
      time_saved: timeSaved.scope,
      by_tool: tools.scope,
      by_model: chat?.daily_cost_by_model && Object.keys(chat.daily_cost_by_model).length ? 'window' : 'all-time',
      // Without the per-service daily map this is the lifetime split, as billed.
      cost_composition: ce?.by_service_daily ? 'window' : 'all-time',
    },
    cost_composition,
    by_category: {
      agent_run: { cost: agentRun.cost, user_messages: agentRun.messages, convs: agentRun.conversations },
      agent_chat: { cost: agentChat.cost, user_messages: agentChat.messages, convs: agentChat.conversations },
      plain_chat: { cost: plain.cost, user_messages: plain.messages, convs: plain.conversations },
    },
    by_user_top,
    by_agent_top,
    by_tool: Object.fromEntries(tools.rows.map((r) => [r.tool, r.calls])),
    by_model: modelTotalsInWindow(chat, win),
    top_conversations,
    top_scheduled_runs,
  };

  const credits = selectCredits(data, win, isAggregate(data) ? undefined : contractOf(data));
  if (credits) {
    view.credits = {
      available: true,
      used_in_window: credits.window.value,
      current_month: credits.monthLabel,
      current_month_used: credits.month.value,
      current_month_allocation: credits.allocation?.value,
      available_balance: credits.balance
        ? credits.balance.value
        : (credits.prepaid?.value ?? 0) - (credits.owed?.value ?? 0),
      credit_usd: credits.creditUsd,
      not_billed_over: credits.notBilledOver?.value,
    };
    if (isAggregate(data)) {
      view.credits.owed = credits.owed?.value;
      view.credits.prepaid = credits.prepaid?.value;
      view.credits.collectible_stacks = credits.collectibleStacks ?? undefined;
      view.credits.clients_with_ledger = credits.stacksMetered;
      view.credits.stacks_behind = credits.stacksBehind;
    } else {
      view.credits.collectible = credits.collectible ?? undefined;
    }
  }

  const apps = selectApps(data, win);
  if (apps) {
    view.apps = {
      inventory: apps.inventory,
      has_history: apps.hasHistory,
      history_from: apps.historyFrom,
      runs_in_window: apps.window?.runs.value,
      credits_in_window: apps.window?.credits.value,
      publishes_in_window: apps.window?.publishes.value,
    };
  }

  if (isAggregate(data)) {
    // The rows are the stacks the view counts, as the screen lists them.
    const members = new Set(data.members ?? []);
    view.by_client = buildByClientRowsWindowed(
      snapshots.filter((s) => members.has(s.client)),
      win,
      contractOf
    );
  }

  return view;
}

/** The spend of a summed view's hidden Inactive stacks in the window, for the export. */
function inactiveSpendExport(data: DashboardView, win: WindowState): DashboardWindowView['spend']['inactive'] {
  const hidden = selectInactiveSpend(data, win);
  if (!hidden) return undefined;
  return { stacks: hidden.stacks, inferred_total: hidden.spend, raw_ce_total: hidden.awsBill, chat_llm: hidden.chat };
}

/**
 * The whole export: every stack and account, and each fleet-wide view (the
 * fixed ones and one for each partner), across the preset windows and one
 * "all" window per view.
 */
export function buildDashboardExport(snapshots: ClientSnapshot[], opts: ViewOptions = {}): DashboardExportBundle {
  const contractOf = opts.contractOf ?? snapshotContract;
  const windowStates: WindowState[] = DEFAULT_WINDOW_DAYS.map((n) => setLastNDays(n));
  const views: Record<string, DashboardExportClientEntry> = {};

  // Each view also gets an "all" window covering everything its data holds.
  // Its start differs from view to view.
  const windowsFor = (view: DashboardView): Array<[string, WindowState]> => {
    const out: Array<[string, WindowState]> = windowStates.map((w) => [`${windowDays(w)}d`, w]);
    const all = setAllData(view);
    if (all) out.push(['all', all]);
    return out;
  };
  const windowViews = (view: DashboardView) =>
    Object.fromEntries(windowsFor(view).map(([k, w]) => [k, buildDashboardWindowView(view, w, snapshots, contractOf)]));

  // An Inactive stack the screen hides has no entry of its own. Its spend is in the views' spend.
  const hidden = hiddenBy(opts);
  for (const s of snapshots) {
    if (hidden.has(s.client)) continue;
    // A standalone customer's bill is cut to Numa's spend, as on screen.
    const view = snapshotForDisplay(s);
    const terms = isInfraAccount(s) ? null : contractOf(s);
    views[s.client] = {
      client: s.client,
      is_aggregate: false,
      client_config: s.client_config,
      contract: terms
        ? {
            contract: terms.contract ?? null,
            partner: terms.partner ?? null,
            billing: terms.billing ?? null,
            collectible: terms.collectible,
            inactive: terms.inactive,
            product: productOf(s),
          }
        : undefined,
      generated_at: s.generated_at,
      window_views: windowViews(view),
    };
  }

  const viewDefs = opts.views ?? fleetViews(snapshots, contractOf);
  for (const def of viewDefs) {
    const { view } = viewForSelection(def.id, snapshots, { ...opts, views: viewDefs });
    if (!view || !isAggregate(view)) continue;
    views[def.id] = {
      client: def.id,
      is_aggregate: true,
      generated_at: view.oldest_generated_at,
      newest_generated_at: view.newest_generated_at,
      stale_stacks: view.stale_stacks,
      window_views: windowViews(view),
    };
  }

  return {
    exported_at: new Date().toISOString(),
    schema_version: '1.2',
    window_presets: [...DEFAULT_WINDOW_DAYS],
    tiers_loaded: opts.tiers ?? ['users', 'agents', 'services', 'inventory', 'apps', 'conversations'],
    views,
  };
}
